/**
 * Alert-feed store — capped FIFO of Alert-Router alerts (distinct from the raw
 * event stream in `alerts.ts`, which drives the drone Events tab). This store
 * drives the Live Alerts pane. Per FLYsafe MQTT Topic doc.
 */
import type { AlertEnvelope } from '@/lib/mqtt/schemas/alerts';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

const ALERT_CAP = 200;

export interface AlertFeedItem extends AlertEnvelope {}

interface AlertFeedStore {
  items: AlertFeedItem[];
  push: (a: AlertEnvelope) => void;
  acknowledge: (alertId: string, user: string) => void;
  dismiss: (alertId: string) => void;
  clear: () => void;
}

export const useAlertFeedStore = create<AlertFeedStore>()(
  devtools(
    (set) => ({
      items: [],
      push: (a) =>
        set((s) => {
          // dedupe by alert_id (re-published alerts update in place)
          const filtered = s.items.filter((i) => i.alert_id !== a.alert_id);
          return { items: [{ ...a }, ...filtered].slice(0, ALERT_CAP) };
        }),
      acknowledge: (alertId, user) =>
        set((s) => ({
          items: s.items.map((i) =>
            i.alert_id === alertId
              ? { ...i, acked_at: new Date().toISOString(), acked_by: user }
              : i,
          ),
        })),
      dismiss: (alertId) => set((s) => ({ items: s.items.filter((i) => i.alert_id !== alertId) })),
      clear: () => set({ items: [] }),
    }),
    { name: 'alertFeed' },
  ),
);
